import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Film,
  Loader2,
  Play,
  Pause,
  Sparkles,
  Volume2,
  VolumeX,
  Wand2,
} from "lucide-react";
import { stockFootageAPI } from "../lib/api";

// Edge TTS Voice Options (FREE)
const EDGE_TTS_VOICES = [
  { id: "en_us_female", name: "American Female (Jenny)", language: "English (US)", gender: "female" },
  { id: "en_us_male", name: "American Male (Guy)", language: "English (US)", gender: "male" },
  { id: "en_us_female_warm", name: "American Female Warm (Aria)", language: "English (US)", gender: "female" },
  { id: "en_us_male_friendly", name: "American Male Friendly (Chris)", language: "English (US)", gender: "male" },
  { id: "en_gb_female", name: "British Female (Sonia)", language: "English (UK)", gender: "female" },
  { id: "en_gb_male", name: "British Male (Ryan)", language: "English (UK)", gender: "male" },
  { id: "en_au_female", name: "Australian Female (Natasha)", language: "English (AU)", gender: "female" },
  { id: "en_au_male", name: "Australian Male (William)", language: "English (AU)", gender: "male" },
];

/**
 * StockFootageToggle - Simplified automatic stock footage component
 * 
 * When enabled, the app automatically finds and uses relevant stock footage
 * based on the script. User only needs to choose voice options.
 */
export function StockFootageToggle({
  isEnabled,
  onToggle,
  onSettingsChange,
  clonedVoices = [],
}) {
  // Voice tier selection
  const [voiceTier, setVoiceTier] = useState("free");
  const [selectedFreeVoice, setSelectedFreeVoice] = useState("en_us_female");
  const [selectedPremiumVoice, setSelectedPremiumVoice] = useState(null);
  
  // Voice preview
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [loadingVoicePreview, setLoadingVoicePreview] = useState(null);
  const voiceAudioRef = useRef(null);

  // Update parent with settings
  useEffect(() => {
    onSettingsChange?.({
      enabled: isEnabled,
      autoMode: true, // Always automatic
      voiceTier,
      voiceId: voiceTier === "free" ? selectedFreeVoice : 
               voiceTier === "premium" ? selectedPremiumVoice : null,
    });
  }, [isEnabled, voiceTier, selectedFreeVoice, selectedPremiumVoice, onSettingsChange]);

  // Voice preview handler
  const handleVoicePreview = async (voiceId) => {
    if (playingVoiceId === voiceId) {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current.currentTime = 0;
      }
      setPlayingVoiceId(null);
      return;
    }
    
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
    }
    
    setLoadingVoicePreview(voiceId);
    
    try {
      const token = localStorage.getItem('auth_token');
      const previewUrl = stockFootageAPI.getVoicePreviewUrl(voiceId);
      
      const response = await fetch(previewUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load preview');
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const audio = new Audio();
      audio.src = blobUrl;
      voiceAudioRef.current = audio;
      
      audio.onended = () => {
        setPlayingVoiceId(null);
        URL.revokeObjectURL(blobUrl);
      };
      
      audio.onerror = () => {
        setPlayingVoiceId(null);
        toast.error("Failed to play voice preview");
      };
      
      await audio.play();
      setPlayingVoiceId(voiceId);
    } catch (error) {
      toast.error("Failed to load voice preview");
    } finally {
      setLoadingVoicePreview(null);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
      }
    };
  }, []);

  return (
    <Card className="border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-pink-900/20">
      <CardContent className="p-4">
        {/* Toggle Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Film className="w-5 h-5 text-white" />
            </div>
            <div>
              <Label className="text-white font-semibold flex items-center gap-2">
                Use Stock Footage
                <Badge variant="outline" className="text-xs text-purple-400 border-purple-400/30">
                  <Wand2 className="w-3 h-3 mr-1" />
                  Auto
                </Badge>
              </Label>
              <p className="text-xs text-muted-foreground">
                AI automatically finds matching visuals for your content
              </p>
            </div>
          </div>
          <Switch
            checked={isEnabled}
            onCheckedChange={onToggle}
            data-testid="stock-footage-toggle"
          />
        </div>

        {/* Voice Options - Show when enabled */}
        <AnimatePresence>
          {isEnabled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 pt-4 border-t border-border/30"
            >
              {/* How it works */}
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <p className="text-xs text-purple-300">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  <strong>How it works:</strong> We'll analyze your script and automatically find 
                  relevant stock videos from our library. Just choose your voice option below.
                </p>
              </div>

              {/* Voice Tier Selection */}
              <div className="space-y-3">
                <Label className="text-white text-sm flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-emerald-400" />
                  Voiceover Option
                </Label>
                
                <div className="grid grid-cols-3 gap-2">
                  {/* No Voiceover */}
                  <button
                    type="button"
                    onClick={() => setVoiceTier("none")}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      voiceTier === "none"
                        ? "border-emerald-500 bg-emerald-500/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <VolumeX className="w-4 h-4 text-emerald-400" />
                      <span className="font-medium text-white text-sm">No Voice</span>
                    </div>
                    <Badge className="text-[10px] bg-green-500/20 text-green-400 border-0">FREE</Badge>
                  </button>
                  
                  {/* Free Voice (Edge TTS) */}
                  <button
                    type="button"
                    onClick={() => setVoiceTier("free")}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      voiceTier === "free"
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Volume2 className="w-4 h-4 text-cyan-400" />
                      <span className="font-medium text-white text-sm">Free Voice</span>
                    </div>
                    <Badge className="text-[10px] bg-green-500/20 text-green-400 border-0">FREE</Badge>
                  </button>
                  
                  {/* Premium Voice (ElevenLabs) */}
                  <button
                    type="button"
                    onClick={() => setVoiceTier("premium")}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      voiceTier === "premium"
                        ? "border-purple-500 bg-purple-500/10"
                        : "border-border/50 bg-secondary/30 hover:border-border"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      <span className="font-medium text-white text-sm">Premium</span>
                    </div>
                    <Badge className="text-[10px] bg-purple-500/20 text-purple-400 border-0">PAID</Badge>
                  </button>
                </div>
                
                {/* Free Voice Selection */}
                {voiceTier === "free" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="pt-3"
                  >
                    <Label className="text-white/70 text-xs mb-2 block flex items-center gap-2">
                      Select Voice
                      <span className="text-cyan-400/60">Click ▶ to preview</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                      {EDGE_TTS_VOICES.map((voice) => (
                        <div
                          key={voice.id}
                          className={`relative p-2 rounded-lg text-left text-xs transition-all cursor-pointer ${
                            selectedFreeVoice === voice.id
                              ? "bg-cyan-500/20 border-cyan-500 border"
                              : "bg-white/5 border-white/10 border hover:bg-white/10"
                          }`}
                          onClick={() => setSelectedFreeVoice(voice.id)}
                        >
                          <div className="font-medium text-white truncate pr-7">
                            {voice.name.split("(")[0].trim()}
                          </div>
                          <div className="text-muted-foreground text-[10px]">{voice.language}</div>
                          
                          {/* Preview button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVoicePreview(voice.id);
                            }}
                            disabled={loadingVoicePreview === voice.id}
                            className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                              playingVoiceId === voice.id 
                                ? "bg-cyan-500 text-white" 
                                : "bg-white/10 text-white/60 hover:bg-white/20"
                            }`}
                          >
                            {loadingVoicePreview === voice.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : playingVoiceId === voice.id ? (
                              <Pause className="w-2.5 h-2.5" />
                            ) : (
                              <Play className="w-2.5 h-2.5" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
                
                {/* Premium Voice Selection */}
                {voiceTier === "premium" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="pt-3"
                  >
                    {clonedVoices.length > 0 ? (
                      <>
                        <Label className="text-white/70 text-xs mb-2 block">Your Cloned Voices</Label>
                        <div className="flex flex-wrap gap-2">
                          {clonedVoices.map((voice) => (
                            <button
                              key={voice.voice_id}
                              type="button"
                              onClick={() => setSelectedPremiumVoice(voice.voice_id)}
                              className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                                selectedPremiumVoice === voice.voice_id
                                  ? "bg-purple-500/20 border-purple-500 border text-purple-300"
                                  : "bg-white/5 border-white/10 border text-white/70 hover:bg-white/10"
                              }`}
                            >
                              🎙️ {voice.name}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-purple-300/70 p-2 bg-purple-500/10 rounded">
                        Clone your voice in Settings to use it here, or we'll use a premium ElevenLabs voice.
                      </p>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

export default StockFootageToggle;
